import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, money0, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { can } from "../lib/roles";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill } from "../components/UI";
import { useSortable } from "../lib/sortable";

const STATUS_TONE = {
  "approved": "green",
  "setup-in-progress": "amber",
  "needs-recheck": "red",
};
const STATUS_LABEL = {
  "approved": "APPROVED",
  "setup-in-progress": "SETUP IN PROGRESS",
  "needs-recheck": "NEEDS A RE-CHECK",
};

// Backdrop click helper that ignores clicks that started INSIDE the modal
// (fixes the "select text inside modal → modal closes" bug).
function useSafeBackdrop(onClose) {
  const [downOnBackdrop, setDownOnBackdrop] = useState(false);
  return {
    onMouseDown: (e) => setDownOnBackdrop(e.target === e.currentTarget),
    onMouseUp: (e) => {
      if (downOnBackdrop && e.target === e.currentTarget) onClose();
      setDownOnBackdrop(false);
    },
  };
}

function EditCustomerDialog({ customer, onClose, onSaved, canEdit }) {
  const [f, setF] = useState({
    legal_name: customer.legal_name || "",
    trading_name: customer.trading_name || "",
    abn: customer.abn || "",
    site_address: customer.site_address || "",
    account_status: customer.account_status || "setup-in-progress",
    is_prospect: !!customer.is_prospect,
    // Optional[bool] on the server. Kept as three explicit UI values:
    //   'default' → null on the wire (follow global)
    //   'per-machine' → false
    //   'consolidate' → true
    invoicing_pref: customer.consolidate_invoices === true ? "consolidate"
                  : customer.consolidate_invoices === false ? "per-machine"
                  : "default",
    contacts: (customer.contacts && customer.contacts.length > 0
      ? customer.contacts
      : [{ name: "", role: "Site", phone: "", email: "" }]
    ).map(c => ({ name: c.name || "", role: c.role || "", phone: c.phone || "", email: c.email || "" })),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const backdrop = useSafeBackdrop(onClose);

  const setContact = (i, k, v) => {
    const cs = f.contacts.slice();
    cs[i] = { ...cs[i], [k]: v };
    setF({ ...f, contacts: cs });
  };
  const addContact = () => setF({ ...f, contacts: [...f.contacts, { name: "", role: "", phone: "", email: "" }] });
  const removeContact = (i) => setF({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!f.legal_name.trim()) { setErr("Legal name is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        legal_name: f.legal_name.trim(),
        trading_name: f.trading_name || null,
        abn: (f.abn || "").trim() || null,
        site_address: f.site_address || null,
        account_status: f.account_status,
        is_prospect: !(f.abn || "").trim(),
        contacts: f.contacts.filter(c => c.name.trim()).map(c => ({
          name: c.name.trim(), role: c.role || null,
          phone: c.phone || null, email: c.email || null,
        })),
      };
      // consolidate_invoices — three explicit states. Always sent so the
      // server can distinguish "leave alone" (payload key absent) from
      // "clear back to default" (payload key present with value null).
      //   "default"     → null  → server $unsets the field
      //   "per-machine" → false → server $sets to False
      //   "consolidate" → true  → server $sets to True
      payload.consolidate_invoices =
        f.invoicing_pref === "per-machine" ? false
        : f.invoicing_pref === "consolidate" ? true
        : null;
      await api.patch(`/customers/${customer.id}`, payload);
      onSaved(); onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="edit-customer-dialog" {...backdrop}>
      <div className="ach-modal" style={{maxWidth: 780}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Edit customer — {customer.legal_name}</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}}>Close</button>
        </div>
        <div className="ach-modal__body">
          <div className="ach-form-grid">
            <div className="ach-field"><label>Legal name *</label>
              <input value={f.legal_name} onChange={(e)=>setF({...f, legal_name:e.target.value})} data-testid="ec-legal-name" />
            </div>
            <div className="ach-field"><label>Trading name</label>
              <input value={f.trading_name} onChange={(e)=>setF({...f, trading_name:e.target.value})} data-testid="ec-trading-name" />
            </div>
            <div className="ach-field"><label>ABN</label>
              <input value={f.abn} onChange={(e)=>setF({...f, abn:e.target.value})} inputMode="numeric" data-testid="ec-abn" placeholder="Leave blank to keep as a prospect" />
            </div>
            <div className="ach-field"><label>Account status</label>
              <select value={f.account_status} onChange={(e)=>setF({...f, account_status:e.target.value})} data-testid="ec-status">
                <option value="setup-in-progress">Setup in progress</option>
                <option value="approved">Approved</option>
                <option value="needs-recheck">Needs a re-check</option>
              </select>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Site / main address</label>
              <textarea rows={2} value={f.site_address} onChange={(e)=>setF({...f, site_address:e.target.value})} data-testid="ec-address" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}} data-testid="ec-invoicing-pref">
              <label>Invoicing</label>
              <div style={{display:"flex", flexDirection:"column", gap:6, marginTop:4}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer"}}>
                  <input type="radio" name="invoicing_pref" value="default"
                         checked={f.invoicing_pref === "default"}
                         onChange={()=>setF({...f, invoicing_pref:"default"})}
                         data-testid="ec-invoicing-default"
                         style={{width:"auto", flex:"0 0 auto"}} />
                  {/*
                    Dynamic tail added Sep 2026 for Friction Batch (b). The global
                    default is served by GET /customers as `global_consolidate_default`
                    so this radio no longer needs to hit Director-only Xero settings.
                    If the tail can't be computed (undefined/null), the label drops
                    back to the plain wording rather than showing "currently undefined".

                    The `width:auto, flex:0 0 auto` on each radio is necessary
                    because .ach-field input has `width: 100%` in App.css — that
                    rule was meant for text inputs but also targets radios and
                    (before this override) stretched each radio's clickable area
                    across the row, pushing the label text far to the right.
                  */}
                  {customer.global_consolidate_default === true
                    ? "Follow the company default — currently one combined invoice"
                    : customer.global_consolidate_default === false
                    ? "Follow the company default — currently one invoice per machine"
                    : "Follow the company default"}
                </label>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer"}}>
                  <input type="radio" name="invoicing_pref" value="consolidate"
                         checked={f.invoicing_pref === "consolidate"}
                         onChange={()=>setF({...f, invoicing_pref:"consolidate"})}
                         data-testid="ec-invoicing-consolidate"
                         style={{width:"auto", flex:"0 0 auto"}} />
                  Always combine into one invoice for this customer
                </label>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer"}}>
                  <input type="radio" name="invoicing_pref" value="per-machine"
                         checked={f.invoicing_pref === "per-machine"}
                         onChange={()=>setF({...f, invoicing_pref:"per-machine"})}
                         data-testid="ec-invoicing-per-machine"
                         style={{width:"auto", flex:"0 0 auto"}} />
                  Always one invoice per machine
                </label>
              </div>
              <div className="helper">Only applies to this customer. Overrides the global default set on the Xero connection page.</div>
            </div>
          </div>

          <div style={{marginTop:14}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
              <h3 style={{fontSize:14, fontWeight:600, margin:0}}>People to talk to</h3>
              <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:11}} onClick={addContact} data-testid="ec-add-contact">+ Add contact</button>
            </div>
            {f.contacts.map((c, i) => (
              <div key={i} className="ach-form-grid" style={{gridTemplateColumns:"1.4fr 1fr 1fr 1.4fr auto", gap:8, marginBottom:8, alignItems:"end"}} data-testid={`ec-contact-${i}`}>
                <div className="ach-field"><label>{i === 0 ? "Name" : ""}</label>
                  <input value={c.name} onChange={(e)=>setContact(i, "name", e.target.value)} data-testid={`ec-contact-${i}-name`} placeholder="e.g. Sarah Wilson" />
                </div>
                <div className="ach-field"><label>{i === 0 ? "Role" : ""}</label>
                  <input value={c.role} onChange={(e)=>setContact(i, "role", e.target.value)} data-testid={`ec-contact-${i}-role`} placeholder="Site / Accounts" />
                </div>
                <div className="ach-field"><label>{i === 0 ? "Phone" : ""}</label>
                  <input type="tel" inputMode="tel" autoComplete="tel" value={c.phone} onChange={(e)=>setContact(i, "phone", e.target.value)} data-testid={`ec-contact-${i}-phone`} placeholder="0400 123 456" />
                </div>
                <div className="ach-field"><label>{i === 0 ? "Email" : ""}</label>
                  <input type="email" value={c.email} onChange={(e)=>setContact(i, "email", e.target.value)} data-testid={`ec-contact-${i}-email`} />
                </div>
                <button className="ach-btn ach-btn--ghost" style={{padding:"6px 8px", fontSize:11}} onClick={()=>removeContact(i)} data-testid={`ec-contact-${i}-remove`} disabled={f.contacts.length === 1}>Remove</button>
              </div>
            ))}
          </div>
          {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, marginTop:10}}>{err}</div>}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="ec-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save}
            disabled={busy || !canEdit}
            title={canEdit ? undefined : "You don't have permission to edit customers."}
            data-testid="ec-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const { user } = useAuth();
  const canEdit = can(user, "customers.edit");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  // Column sorting (added Sep 2026 evening — same behaviour as Hires and
  // Machines via src/lib/sortable.js).
  const { sort, toggleSort, sortArrow, applySort } = useSortable();
  const nav = useNavigate();

  const [showArchived, setShowArchived] = useState(false);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  const load = () => api.get(`/customers${showArchived ? "?include_archived=1" : ""}`).then(r => setCustomers(r.data));
  useEffect(() => {
    load();
  }, [showArchived]);

  const archiveCustomer = async (c) => {
    try {
      await api.post(`/customers/${c.id}/archive`, {});
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === "object" ? detail.message : (detail || e.message));
    }
  };
  const restoreCustomer = async (c) => {
    try {
      await api.post(`/customers/${c.id}/restore`, {});
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const filtered = customers.filter(c => {
    if (onlyIncomplete && !c.contact_details_missing) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.legal_name || "").toLowerCase().includes(s) || (c.abn || "").includes(s) || (c.trading_name || "").toLowerCase().includes(s);
  });
  // Sort AFTER filter — search narrows what's visible, sort orders it.
  const sorted = applySort(filtered, (c, key) => {
    switch (key) {
      case "name":   return (c.legal_name || "").toLowerCase();
      case "abn":    return (c.abn || "").toLowerCase();
      case "status": return (c.account_status || "").toLowerCase();
      case "owing":  return Number(c.owing_amount) || 0;
      default:       return "";
    }
  });
  const totalOwing = customers.reduce((s, c) => s + (c.owing_amount || 0), 0);
  const approvedCount = customers.filter(c => c.account_status === "approved" && !c.is_prospect).length;
  const recheckCount = customers.filter(c => c.account_status === "needs-recheck").length;
  const prospectCount = customers.filter(c => c.is_prospect).length;
  const missingContactsCount = customers.filter(c => c.contact_details_missing).length;

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Everyone we hire to or might hire to"
        actions={<>
          <label style={{display:"inline-flex", alignItems:"center", gap:6, fontSize:12, marginRight:12, color:"var(--ach-text-2)"}}>
            <input type="checkbox" checked={showArchived} onChange={(e)=>setShowArchived(e.target.checked)} data-testid="customers-show-archived" />
            Show archived
          </label>
          <button className="ach-btn ach-btn--primary" onClick={()=>nav("/new-customer")} data-testid="btn-new-customer">+ Add a customer</button>
        </>}
      />

      {missingContactsCount > 0 && (
        <div style={{
          padding:"10px 14px", marginBottom:12, borderRadius:8, fontSize:13,
          background:"var(--ach-amber-tint, #fff6e0)",
          border:"1px solid var(--ach-amber, #c88c13)",
          display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
        }} data-testid="prospects-missing-banner">
          <strong style={{color:"var(--ach-amber, #c88c13)"}}>⚠ {missingContactsCount} prospect{missingContactsCount !== 1 ? "s" : ""} without contact details</strong>
          <span style={{color:"var(--ach-text-2)"}}>No phone or email on any contact row — they can't be reminded or quoted-with-contact. Fill the details in before you turn them into a hire.</span>
          <div style={{flex:1}} />
          <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={()=>setOnlyIncomplete(!onlyIncomplete)}
                  data-testid="prospects-missing-toggle">
            {onlyIncomplete ? "Show all →" : "Show only these →"}
          </button>
        </div>
      )}

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(4, minmax(0,1fr))"}}>
        <KpiCard tone="neutral" number={customers.length} label="On the books" testid="cust-kpi-total" />
        <KpiCard tone="green" number={approvedCount} label="Approved accounts" testid="cust-kpi-approved" />
        <KpiCard tone="blue" number={prospectCount} label="Prospects (quote only)" testid="cust-kpi-prospects" />
        <KpiCard tone="amber" number={money0(totalOwing)} label="Total money owed to us" testid="cust-kpi-owed" />
      </div>

      <div className="ach-card">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
          <h2 className="ach-card__title" style={{margin: 0}}>Everyone we deal with</h2>
          <input placeholder="Search name or ABN…" value={q} onChange={(e)=>setQ(e.target.value)}
            data-testid="cust-search"
            style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"8px 12px", fontSize:13, width: 260}} />
        </div>
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                {/* Same sort interaction as Hires and Machines — see src/lib/sortable.js */}
                <th onClick={()=>toggleSort("name")}   style={{cursor:"pointer", userSelect:"none"}} data-testid="customer-col-sort-name">Legal name{sortArrow("name")}</th>
                <th onClick={()=>toggleSort("abn")}    style={{cursor:"pointer", userSelect:"none"}} data-testid="customer-col-sort-abn">ABN{sortArrow("abn")}</th>
                <th>Contacts</th>
                <th onClick={()=>toggleSort("status")} style={{cursor:"pointer", userSelect:"none"}} data-testid="customer-col-sort-status">Account{sortArrow("status")}</th>
                <th onClick={()=>toggleSort("owing")}  className="money" style={{cursor:"pointer", userSelect:"none"}} data-testid="customer-col-sort-owing">Money owed{sortArrow("owing")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                  <td>
                    <div className="unit-num">{c.legal_name}</div>
                    {c.trading_name && <div className="subtitle">t/a {c.trading_name}</div>}
                    {c.is_prospect && <StatusPill tone="blue">PROSPECT</StatusPill>}
                    {c.contact_details_missing && <StatusPill tone="amber" testid={`missing-contact-${c.id}`}>MISSING CONTACT</StatusPill>}
                    {c.archived_at && <StatusPill tone="neutral" testid={`archived-${c.id}`}>ARCHIVED</StatusPill>}
                  </td>
                  <td>{c.abn || <span style={{color:"var(--ach-text-2)"}}>—</span>}</td>
                  <td>
                    {(c.contacts || []).slice(0, 1).map((ct, i) => (
                      <div key={i}>{ct.name} <span className="subtitle">{ct.role}</span></div>
                    ))}
                    {(c.contacts || []).length > 1 && <div className="subtitle">+{c.contacts.length - 1} more</div>}
                  </td>
                  <td><StatusPill tone={STATUS_TONE[c.account_status]}>{STATUS_LABEL[c.account_status]}</StatusPill></td>
                  <td className="money">{c.owing_amount > 0 ? money(c.owing_amount) : "—"}</td>
                  <td style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                    <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:11}} onClick={()=>setEditing(c)} disabled={!canEdit} title={canEdit ? undefined : "You don't have permission to edit customers."} data-testid={`edit-customer-${c.id}`}>Edit</button>
                    {!c.archived_at && canEdit && (
                      <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:11}} onClick={()=>archiveCustomer(c)} data-testid={`archive-customer-${c.id}`} title="Archive — hides from the default list; a customer with linked hires or converted quotes cannot be archived.">Archive</button>
                    )}
                    {c.archived_at && canEdit && (
                      <button className="ach-btn ach-btn--ghost" style={{padding:"4px 10px", fontSize:11}} onClick={()=>restoreCustomer(c)} data-testid={`restore-customer-${c.id}`}>Restore</button>
                    )}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{textAlign:"center", padding:24, color:"var(--ach-text-2)"}}>No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditCustomerDialog customer={editing} onClose={()=>setEditing(null)} onSaved={load} canEdit={canEdit} />}
    </div>
  );
}
